import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { TaxBadge } from './TaxBadge';
import { formatTaxRate } from '@/lib/tax/tax-utils';
import type { AppliedTaxPolicy, TaxResolutionResult } from '@/types/tax';

interface TaxResolutionSectionProps {
  resolution: TaxResolutionResult | null;
  loading?: boolean;
  /** Tax Policy layer: the rule frozen into the document's taxSummary. */
  policy?: AppliedTaxPolicy | null;
  /** When set, renders a "Change tax group" action (the MANUAL writer). */
  onChangeTaxGroup?: () => void;
}

function policyEffectLabel(policy: AppliedTaxPolicy): string {
  if (policy.taxableBases.includes('all')) return policy.name;
  if (policy.taxableBases.length === 0) return `${policy.name} · nothing taxable`;
  return `${policy.name} · only ${policy.taxableBases.join(' + ')} taxable`;
}

export function TaxResolutionSection({
  resolution,
  loading,
  policy,
  onChangeTaxGroup,
}: TaxResolutionSectionProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Resolving tax rate...</span>
      </div>
    );
  }

  if (!resolution) {
    return (
      <div className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
        Enter a service address ZIP code to resolve the tax rate
      </div>
    );
  }

  const group = resolution.group;
  const appliedPolicy = policy ?? resolution.policy ?? null;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
      <TaxBadge resolvedFrom={resolution.resolvedFrom} />
      {group ? (
        <>
          <span className="font-medium">{group.name}</span>
          <Badge variant="secondary" className="text-xs">
            {formatTaxRate(group.combinedRate)}
          </Badge>
        </>
      ) : (
        <span className="text-muted-foreground">No tax group resolved</span>
      )}
      {appliedPolicy ? (
        <Badge
          variant="outline"
          className="border-transparent bg-indigo-100 text-[11px] text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300"
          title="Tax rule applied at pricing time (Settings → Taxes → Rules)"
        >
          {policyEffectLabel(appliedPolicy)}
        </Badge>
      ) : null}
      {onChangeTaxGroup ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="ml-auto h-6 px-2 text-xs"
          onClick={onChangeTaxGroup}
        >
          Change tax group
        </Button>
      ) : null}
    </div>
  );
}
